/* eslint-disable @typescript-eslint/explicit-function-return-type */
/**
 * An instance or execution of storyboard plan.
 */
import { Promise as BPromise } from 'bluebird'
import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { Entity, ISaveActionProps } from '../../entity'
import { EntityType } from '../entity-type'
import { Event } from './storyboard-execution-model'
import { Mapping, Mappings } from './storyboard-plan-model'
import { StoryboardScene } from './storyboard-scene'
import {
  SharedContext,
  EventType as SharedContextEvent,
  TRANSIENT_PREFIX,
} from './storyboard-shared-context'
import { StoryboardStory } from './storyboard-story'
import { StoryboardTask } from './storyboard-task'
import { RequestMethod, Store } from '../../../models/store'
import { PlatformType } from './storyboard-plan'
import { getDeviceInfoProvider } from '../../device'
import { jsonCloneDeep, waitUntil, trueTimeNow } from '../../../helpers/utils'
import * as Logger from '../../../helpers/logger'
import { FormulaRegistry } from '../../../helpers/formulas'
import { eventDifference, eventJoinOperations } from './storyboard-utils'
import { Compressor } from '../../compressor'
import { StopWatch } from '../../../helpers/stop-watch'
import { ConfigKey, getConfig } from '../../config'

export enum StoryboardExecutionStatus {
  PENDING = 'Pending',
  ACTIVE = 'Active',
  PAUSED = 'Paused',
  CANCELED = 'Canceled',
  COMPLETED = 'Completed',
}

export enum EventType {
  CREATE = 'create',
  EDIT = 'edit',
  DETAIL = 'detail',
  SHARE = 'share',
  EXTERNAL = 'external',
  LOG = 'log',
  BREADCRUMB = 'breadcrumb',
  ACTION = 'action',
  NOTIFICATION = 'notification',
  NOTIFICATION_LOG = 'notification_log',
  COMPUTATION = 'core_storyboard_event_computation',
  SYNTHETIC = 'core_storyboard_event_synthetic',
  TASK_GROUP = 'task_group',
  CHANGESET = 'core_storyboard_event_changeSet',
  SESSION_REPLAY_CRUMB = 'core_storyboard_event_sessionReplayCrumb',
  MAP_APPOINTMENT = 'core_yms_carrierAppointment_mapped',
  TRAILER_LOOKUP_RESPONSE = 'core_yms_event_trailerLookup_response',
  ASYNC_SILENT_EDIT = 'asyncSilentEdit',
  EXECUTE_API_DEFINITION_RESULT = 'execute_api_definition_result',
}

export interface IStoryboardNode {
  story: StoryboardStory
  scene?: StoryboardScene
  task?: StoryboardTask
}

export interface IEventSource {
  storyId: string
  sceneId: string
  taskId?: string
}

export const crumbIdFromEventSource = (eventSource: IEventSource): string => {
  const { storyId, sceneId, taskId } = eventSource
  const ids = _.compact([storyId, sceneId, taskId])
  return _.join(ids, '.')
}

export const eventSourceFromNode = (node: IStoryboardNode): IEventSource => {
  return {
    storyId: node?.story?.data?.id || '',
    sceneId: node?.scene?.data?.id || '',
    taskId: node?.task?.data?.id || '',
  }
}

export const crumbIdFromNode = (node: IStoryboardNode): string => {
  return crumbIdFromEventSource(eventSourceFromNode(node))
}

export const PATHS = {
  EVENTS: 'core_storyboard_execution.events',
  PLAN: 'core_storyboard_execution.plan',
  DETAILS: 'core_storyboard_execution.details',
  COMPRESSED_EVENTS: TRANSIENT_PREFIX + '.core_storyboard_execution.events',
}

export class StoryboardExecutionEntity extends EntityType {
  public context: SharedContext
  public storyboardPlan?: Entity

  protected storyboardExecution: any = null
  protected currEvents: Event[] = [] // keep a copy of server events that is synced to sharedcontext

  private locationProvider: () => BPromise<any>
  private isHydrated: boolean = false
  private enableEventLogCompression: boolean = false

  /**
   * A read-only proxy that provides a merged view of a shared context and an entity.
   *
   * It resolves properties from the shared context first, then falls back to the entity's
   * data. This creates a safe, controlled data model intended for read-only consumption via `self`
   *
   * To enforce a one-way data flow, this proxy blocks all property assignments. Any
   * modifications to the underlying data must be performed through an UI components,
   * not by directly mutating this proxy.
   */
  public static createProxy(
    execution: StoryboardExecutionEntity,
    path: (string | symbol)[] = [],
    target: object | null = null
  ) {
    // For nested proxies, we'll pass the resolved value (the array or object) directly.
    const currTarget = target !== null ? target : execution

    const handler: ProxyHandler<object> = {
      get(target, prop, receiver) {
        if (typeof prop === 'symbol') {
          return Reflect.get(target, prop, receiver)
        }

        const absolutePath = path.concat(prop)
        const value = execution.resolveValue(absolutePath)

        // If the value is an object or array, wrap it in a new proxy.
        if ((_.isPlainObject(value) && !_.isEmpty(value)) || _.isArray(value)) {
          return StoryboardExecutionEntity.createProxy(execution, absolutePath, value)
        }

        return value
      },

      set(target, prop, value) {
        const absolutePath = path.concat(prop)
        throw new TypeError(
          `Cannot assign to read-only property '${absolutePath}'. This proxy is immutable.`
        )
      },

      has(target, prop) {
        const absolutePath = path.concat(prop)
        return execution.resolveValue(absolutePath) !== undefined
      },

      deleteProperty(target, prop) {
        const absolutePath = path.concat(prop)
        throw new TypeError(`Cannot delete property '${absolutePath}' from a read-only proxy.`)
      },
    }

    return new Proxy(currTarget, handler)
  }

  constructor({ api, data, entity, entitySchema, options }) {
    super({ api, data, entity, entitySchema })

    if (options?.storyboardPlan) {
      this.storyboardPlan = options?.storyboardPlan
    }

    this.enableEventLogCompression = options?.enableEventLogCompression ?? false
    this.compressEventsLog()
  }

  public setLocationProvider(locationProvider: () => BPromise<any>): void {
    this.locationProvider = locationProvider
  }

  /**
   * Add event to event list and populate its content to the shared context as well
   */
  public processEvent(event: Event) {
    this.context.addEvent(event)
    this.addEvent(event)
  }

  /**
   * Just add the event into the event list without updating the shared context
   */
  public addEvent(event: Event) {
    if (_.isEmpty(event)) {
      return
    }

    // TODO: Temporarily back this out. `outputMappings` may contain
    // undefined, null, or empty objects, which could trigger validation errors later.
    // See ticket VD-20048 for revisiting and properly handling these cases.

    // Immediately update the local state
    // if (event?.outputMappings && _.isNil(event.processedDate)) {
    //   _.forEach(event.outputMappings, ({ value, destination }) => {
    //     this.entity.set(destination, value)
    //   })
    // }

    this.currEvents.push(event)
  }

 /**
   * Creates an event with session replay details to serve as a "crumb" or bookmark.
   * This allows linking directly to this moment in a session replay from insight page
   * It also tracks a corresponding event in the session replay service itself.
   *
   */
  public dropSessionReplayCrumb = async (
    node: IStoryboardNode,
    customDetails: any = {}
  ): Promise<Event | null> => {
    const event = this.createEvent(node, EventType.SESSION_REPLAY_CRUMB)

    try {
      const replayDetails = await this.api.getSettings().getReplaySessionInfoWithTimeout(5000)

      if (!replayDetails) {
        console.warn('Could not fetch session replay details. Crumb will not be dropped.')
        return null
      }

      event.details = { ...replayDetails, ...customDetails }

      const trackDetails =  {
          entityId: this.entity.uniqueId,
          planName: this.planName,
          planId: this.storyboardPlan?.uniqueId,
          storyId: node.story.id,
          ...customDetails,
      }

      this.api
        .getSettings()
        .sessionReplay?.track?.(customDetails.eventName ?? 'WORKFLOW_STARTED', trackDetails)

      console.log('Dropping session replay crumb', event, trackDetails)

      this.addEvent(event)
      return event
    } catch (error) {
      console.error('Failed to drop session replay crumb due to an error:', error)
      return null
    }
  }


  public get planName() {
    return this.get('plan.displayName', 'Unknown')
  }

  public get planVersion() {
    return _.get(this.storyboardPlan, 'core_storyboard_plan.version.versionId')
  }

  /**
   * All the users who has the permission to see this execution
   */
  public get users(): any[] {
    const permissions = this.get('permissions', [])
    return _.map(permissions, (permission) => permission.user) || []
  }

  public get newEvents(): Event[] {
    const currEvents = this.currEvents
    const origEvents = this.executionEvents

    return eventDifference(currEvents, origEvents)
  }

  public get storiesForCurrUser() {
    return this.getStoriesForCurrentUser(this.api.platform)
  }

  public getStoriesForCurrentUser(platform: PlatformType) {
    const currUser = this.api.getSettings().user
    // potential users from plan
    const plan = _.get(this.storyboardPlan, 'core_storyboard_plan', {})
    const presetStories = plan?.getStoriesForCurrUser?.(platform) || []

    if (_.isEmpty(plan)) {
      const entityId = this.get('plan')?.entityId
      console.log('***WARNING*** Storyboard plan is nil in getStoriesForCurrentUser')
      Logger.warn('Storyboard_Plan_Not_Found', {
        platform,
        entityId,
        storyboardPlan: this.storyboardPlan,
      })
    }

    // users already viewed the execution
    const viewedStories = _.chain(this.users || [])
      .filter((user) => _.get(user, 'user.entityId') === currUser.uniqueId)
      .flatMap((user) => plan?.getStories(user.storyId || ''))
      .filter((story) => _.includes(story.platforms, platform))
      .value()

    return _.uniqBy([...presetStories, ...viewedStories], 'id')
  }

  public updateExecutionState = (outputMappings: Mappings, data = {}, source?: IStoryboardNode) => {
    const eventOutputMappings = this.context?.setValueWithMappings(outputMappings, data) ?? []
    const event = this.createEventWithNode(source, 'action')
    event.outputMappings = eventOutputMappings

    this.executionEvents = [event]
  }

  public getEventLogSize = () => {
    return _.size(this.currEvents)
  }

  /********************************************************************************/
  /*
   *  Events
   */
  public createEvent(
    node: IStoryboardNode,
    type: any = 'edit',
    associatedEntities: any[] = []
  ): Event {
    return this.createEventWithNode(node, type, associatedEntities)
  }

  public createEventWithNode(
    node: IStoryboardNode,
    type: any = 'edit',
    associatedEntities: any[] = []
  ): Event {
    const eventSource = eventSourceFromNode(node)
    return this.createEventWithSource(eventSource, type, associatedEntities)
  }

  public createEventWithSource(
    source: IEventSource,
    type: any = 'edit',
    associatedEntities: any[] = []
  ): Event {
    const event: Event = {}
    const settings = this.api.getSettings()

    event.id = uuidv4().toString()
    event.source = source
    event.createdBy = { entityId: settings.getUser().uniqueId }
    const trueTimeOffset = FormulaRegistry.getApplicationContext()?.getTruetimeOffset?.() ?? 0
    event.creationDate = moment().add(trueTimeOffset).toISOString()
    event.eventType = type
    event.associations = _.map(associatedEntities, (entity) => {
      const entityId = _.get(entity, 'uniqueId')
      return { entityId }
    })
    event.deviceInfo = getDeviceInfoProvider().getDeviceInfo(this.planVersion)

    return event
  }

  /********************************************************************************/
  /*
   * Breadcrumb to use for playback purpose
   /


  /**
   * @param node: current scene/task node
   * @param contextMappings: use for debugging purposes to see what local context is being passed into the scene/task
   * @return true if the crumb is dropped
   *         false if the crumb is not added since it is already existed
   */
  public dropViewedCrumb(node: IStoryboardNode): boolean {
    const eventSource = eventSourceFromNode(node)
    const id = crumbIdFromEventSource(eventSource)
    const alreadyViewed = this.isViewed(id)

    if (alreadyViewed) {
      return false
    }

    this.dropCrumb(id, eventSource, { isViewed: true })
    /**
     * TODO (VD-10369): Disable inputMappings capturing for debugging purposes, as the plan
     * could pass a rather large object like itself in the inputMappings. We can revisit
     * this feature once we have a truncation feature in place to ensure size limitations.
     */

    // const currContext = this.context.getValueWithMappings(contextMappings)
    // only capture the inputMappings context on the entry of scene/task, but not on completed
    // since the outputMappings could affect the inputMappings context
    // if (!_.isEmpty(currContext)) {
    //   crumb.inputMappings = [
    //     {
    //       value: currContext
    //     }
    //   ]
    // }
    return true
  }

  public dropCompletedCrumb(node: IStoryboardNode) {
    const eventSource = eventSourceFromNode(node)
    const id = crumbIdFromEventSource(eventSource)
    const currState = this.isCompleted(id)

    if (currState === false) {
      this.dropCrumb(id, eventSource, { isCompleted: true })
    }
  }

  public dropCrumb(id: any, eventSource: any, crumb: any): Event {
    const event = this.createEventWithSource(eventSource, EventType.BREADCRUMB)
    event.outputMappings = [
      {
        destination: id,
        value: {
          breadCrumbs: {
            ...crumb,
          },
        },
      },
    ]
    this.processEvent(event)
    return event
  }

  /**
   * Get the storyboard element(scene/task) status from shared context
   */
  public getCrumbs(id: string) {
    return this.context.getValueWithPath(`${id}.breadCrumbs`) || {}
  }

  public isViewed(id: string) {
    const crumbs = this.getCrumbs(id)
    const { isViewed } = crumbs
    return !!isViewed
  }

  public isCompleted(id: string) {
    const crumbs = this.getCrumbs(id)
    const { isCompleted } = crumbs
    return !!isCompleted
  }

  /**
   * Get the value from the shared context, and fallback to entity's content if not found
   */
  public resolveValue = (path: string | (string | symbol)[], defaultValue?: any): any => {
    //
    // This method was chosen over alternatives like object spreading (`...`) or `_.merge`
    // to provide a performant way to coalesce values from the execution state and a shared context.
    //
    // The problem with object spreading is that it performs a shallow merge. For example, if the
    // base entity has an object like this:
    // {
    //   'facility': {
    //     'name': 'abc',
    //     'denormalizedProperties': { 'location': 'xyz' }
    //   }
    // }
    //
    // And the shared context (representing a change) has this:
    // {
    //   'facility': { 'name': 'abc' }
    // }
    //
    // A spread operation like `{ ...entity.content, ...sharedContext }` would completely
    // overwrite the `facility` object, causing the `denormalizedProperties` to be lost.
    //
    // `_.merge` would solve this by performing a deep merge, but it can be computationally
    // expensive for large state objects and requires creating a new object (or mutating an
    // existing one) on every lookup.
    //
    // Therefore, this `resolveValue` method provides a direct, "read-only" way to get the
    // correct effective value without the overhead of a full merge operation.
    //

    // If the data exists in the shared context, return it immediately.
    const sharedContextData = _.get(this.context.sharedContext, path)
    if (sharedContextData !== undefined) {
      return sharedContextData
    }

    // If not found in the shared context, fall back to the entity's own properties.
    // The first `get` handles standard content, while the second `_.get` provides a
    // failsafe for accessing non-content properties like `settings` or `prevContent`.
    return this.entity.get(path, defaultValue) ?? _.get(this.entity, path, defaultValue)
  }

  /**
   * Builds and returns a common context for all UI elements within the workflow.
   *
   * This context provides access to settings, the entity instance (`self`), and a crucial
   * `resolveValue` function to get the most up-to-date value by checking the shared context
   * first, before falling back to the entity's own properties.
   *
   * Instead of accessing a property directly, like `self.core_yms_execution.facility`,
   * it is recommended to use the proxy function: `resolveValue('core_yms_execution.facility')`.
   */
  public getExecutionContext = (): any => {
    const settings = this.entity.getSettings()
    const proxyEnabled = getConfig(settings, ConfigKey.STORYBOARD_EXECUTION_SELF_PROXY_ENABLED)

    const self = this.entity
    const selfProxy = proxyEnabled ? StoryboardExecutionEntity.createProxy(this) : this.entity

    return {
      _,

      settings,
      self,

      // New approach to access the combined state of shared context and entity content.
      // To minimize regressions, avoid overwriting the existing `self` with a proxy.
      // It's better to introduce this as a new, opt-in feature rather than altering existing behavior.
      selfProxy,

      executionEntityId: self.uniqueId,
      resolveValue: this.resolveValue,
    }
  }

  /**
   * Setup the storyboard execution to run
   * - fetch storyboard plan
   * - Deserialize the event log by uncompressing it
   * - Setup pre-save actions to inject synthetic events and sync the execution
   * - Setup shared context from the event's outputMappings
   */
  public async hydrate() {
    const entityId = this.entity.uniqueId
    if (this.isHydrated) {
      console.log('Execution is already hydrated', entityId)
      return
    }

    StopWatch.start(`Hydrate:${entityId}`)
    if (!this.storyboardPlan) {
      console.log('Fetching storyboard plan', entityId)
      void (await this.fetchStoryboardPlan())
    }

    console.log('Decompress event log if compressed', entityId)
    this.decompressEventsLog()

    this.context = new SharedContext(this.entity)
    this.entity.addListener(Store.RECORD_CHANGED, this.onRefreshHandler.bind(this))

    console.log('Intialize shared context', entityId)
    this.initSharedContext()

    // exempt kiosk execution from saves since it needs to be stateless
    if (this.entity.isKioskExecution) {
      this.entity.saveBlocked = true
    }

    this.syncExecutionEvents = this.syncExecutionEvents.bind(this)
    this.injectSyntheticEvents = this.injectSyntheticEvents.bind(this)

    // add a pre save action to update the execution events before saving back to the server
    this.entity.addPreSaveAction(
      {
        action: this.syncExecutionEvents,
        name: 'syncExecutionEvents',
      },
      true
    )

    // TODO: disable synthetic event due to some regression while testing chamberlain
    // inject synthetic event to track of state changes
    this.entity.addPreSaveAction(
      {
        action: this.injectSyntheticEvents,
        name: 'injectSyntheticEvents',
      },
      true
    )
    this.isHydrated = true

    StopWatch.stop(`Hydrate:${entityId}`)
  }

  /**
   * Compact the workflow and free up resources.  Use when the exeuction is not running
   */
  public dehydrate() {
    this.entity?.removeListeners()
    this.entity?.syncOtherContents()
    this.currEvents = []

    // compact events
    this.compressEventsLog()

    this.isHydrated = false
  }

  public fetchStoryboardPlan() {
    const store = this.api.getStore()
    const storyboardPlan = this.get('plan')

    if (storyboardPlan) {
      console.log('Fetch storyboard plan', this.entity.uniqueId)
      return store
        .getOrFetchRecord(storyboardPlan.entityId)
        .then((entity) => {
          if (entity instanceof Entity) {
            this.storyboardPlan = entity
          } else {
            // when the store is deserialize, it loads all the json into its map table
            // then materializeRecord, create Entity, then save all the Entity records
            // back to the table again.  So if it deserialize the execution before the
            // plan, then the plan is just a pure json and not an Entity object.  Kind
            // of hacky for now
            this.storyboardPlan = new Entity(entity, this.api)
            store.cacheRecord(this.storyboardPlan)
          }
          return this.storyboardPlan
        })
        .catch((e: unknown) => {
          console.error('Unable to fetch plan', storyboardPlan.entityId, e)
        })
    }
    return Promise.reject(new Error('No storyboard plan'))
  }

  private set eventLogCompressed(value: boolean) {
    _.set(this.entity, '_metadata.eventLogCompressed', value)
  }

  private get eventLogCompressed(): boolean {
    return _.get(this.entity, '_metadata.eventLogCompressed', false)
  }
  /********************************************************************************/

  private compressEventsLog() {
    const events = this.entity.get(PATHS.EVENTS, [])

    // early exists if it is already compressed/newly created entity/empty
    if (
      this.entity?.isNew ||
      _.isEmpty(events) ||
      !this.enableEventLogCompression ||
      this.eventLogCompressed
    ) {
      return
    }

    StopWatch.start(`Compress:${this.entity.uniqueId}`)
    const compressedEvents = Compressor.compress(events)
    this.eventLogCompressed = true
    StopWatch.stop(
      `Compress:${this.entity.uniqueId}`,
      `originalSize=${_.size(JSON.stringify(events))},compressedSize=${_.size(compressedEvents)}`
    )
    _.set(this.entity.content, PATHS.COMPRESSED_EVENTS, compressedEvents)
    _.set(this.entity.content, PATHS.EVENTS, [])
  }

  private decompressEventsLog() {
    const compressed = this.entity.get(PATHS.COMPRESSED_EVENTS, '')
    // Conservative check: only exit early if the flag shows the event log is uncompressed
    // and the compressed events list is actually empty
    if (!this.eventLogCompressed && _.isEmpty(compressed)) {
      return
    }
    StopWatch.start(`Decompress:${this.entity.uniqueId}`)
    const events = Compressor.decompress(compressed)
    this.eventLogCompressed = false
    StopWatch.stop(
      `Decompress:${this.entity.uniqueId}`,
      `compressed=${_.size(compressed)},decompressed=${_.size(JSON.stringify(events))}`
    )
    // sanity check
    if (!_.isArray(events)) {
      console.error('Sanity decompressed events check failed', events)
      return
    }

    _.set(this.entity.content, PATHS.COMPRESSED_EVENTS, '')
    _.set(this.entity.content, PATHS.EVENTS, events)
  }

  private getOrigEvents(): any {
    const compressedEvents = _.get(this.entity.origContent, PATHS.COMPRESSED_EVENTS, '')

    // compressed events should be empty since it is already hydrate.  If it is not,
    // then it could be a potential serious issue
    if (!_.isEmpty(compressedEvents)) {
      // something really serious wrong here, events should be an array
      const isCompressed = Compressor.isCompressed(compressedEvents)
      Logger.warn('Compressed_events_error', {
        uniqueId: this.entity?.uniqueId,
        isCompressed: isCompressed,
        message: 'The original content compressed_events field should be empty',
      })

      console.error(
        'Events are not in array format',
        this.entity.uniqueId,
        isCompressed,
        compressedEvents
      )
      return isCompressed ? Compressor.decompress(compressedEvents) : []
    }

    return _.get(this.entity.origContent, PATHS.EVENTS, [])
  }

  /**
   * initial the shared context by replay the existing execution events
   */
  private initSharedContext() {
    this.initDefaultSharedContext()

    // populate shared context from execution events
    const events = this.executionEvents
    const sortedEvents = this.sortEvents(events)
    this.context.addEvents(sortedEvents)

    // cached last synced events
    this.currEvents = jsonCloneDeep(events)
  }

  /**
   * Extract the blob from plan and use it as default shared context
   */
  private initDefaultSharedContext() {
    const blobData = _.get(this.storyboardPlan, PATHS.DETAILS, {})
    this.context.setDefault(blobData)
  }

  private get executionEvents(): Event[] {
    return jsonCloneDeep(this.entity.get(PATHS.EVENTS, []))
  }

  private set executionEvents(events: Event[]) {
    const tmpEvents = jsonCloneDeep(events)
    const origEvents = this.getOrigEvents()

    const newEvents = eventDifference(tmpEvents, origEvents)
    this.entity.set(PATHS.EVENTS, [...origEvents, ...newEvents])
  }

  private sortEvents(events: Event[]) {
    return events.sort((a: Event, b: Event) => {
      const aDate = Date.parse(a.creationDate)
      const bDate = Date.parse(b.creationDate)

      return aDate < bDate ? -1 : 1
    })
  }

  /**
   * Once the entity is refresh from the server, merge the changes into the shared context
   */
  private onRefreshHandler() {
    const remoteModifiedDate = this.entity.origContent.modifiedDate
    const localModifiedDate = this.entity.content.modifiedDate

    /**
     * RECORD_CHANGED event could emit from
     * 1. when the entity is uploaded
     * 2. when the formula is finished calculating.  For this case, should not recalculating the shared context
     */

    // infer if refresh is coming from formula context and skip it
    const fromFormulaContext =
      this.entity.isDirty && moment(localModifiedDate).isSame(remoteModifiedDate)
    if (fromFormulaContext) {
      return
    }
    // only refresh shared context on complete and not spammy upload progress (0.01% to 0.99%) RECORD_CHANGE
    const shouldRefresh = this.entity.get('status.progress.percent', 1) === 1

    if (shouldRefresh) {
      this.updateSharedContext()
    }
  }

  /**
   * Update shared context with changes from the server
   */
  private updateSharedContext() {
    // find the diff to merge the events from server to shared context
    const localEvents = this.currEvents
    const remoteEvents = this.executionEvents

    const { leftExclusive: dirtyEvents, rightExclusive: newEvents } = eventJoinOperations(
      localEvents,
      remoteEvents
    )

    // no new events from server, early exit
    if (_.isEmpty(newEvents)) {
      return
    }

    const sortedNewEvents = this.sortEvents([...newEvents])

    // update shared context with the newest event from server
    this.context.addEvents(sortedNewEvents)

    this.currEvents = this.sortEvents([...remoteEvents, ...dirtyEvents])

    // for the case when the server modified the structure data.  When the structured data is updated
    // directly, it does not append the event so emit event and let the subscriber to check for changes
    this.context.emit(SharedContextEvent.Update, {})
  }

  private syncExecutionEvents = async () => {
    // backfill the geolocation on save in one shot
    const events = this.newEvents.filter((event) => _.isEmpty(event.createdAt))
    if (this.locationProvider && !_.isEmpty(events)) {
      try {
        const createdAtLocation = await this.locationProvider()
        events.forEach((event) => _.set(event, 'createdAt', createdAtLocation))
      } catch (err) {
        // only best effort to get the location, if the location is not available, just move on
        console.debug('unable to acquire the location', err)
      }
    }

    // Bump events' creationDate so they reflect the time of enqueuing. The reason this is done is because
    // creationDate is currently being interpreted in metabase metrics for https://withvector.atlassian.net/browse/VD-19037
    // as the enqueue timestamp, used in a delta with server's processedDate, to show us how long tasks are
    // in the client app's queue as a whole.
    //
    // See event list https://stats.withvector.com/question?duration=4#eyJkYXRhc2V0X3F1ZXJ5Ijp7Im5hdGl2ZSI6eyJ0ZW1wbGF0ZS10YWdzIjp7ImR1cmF0aW9uIjp7InR5cGUiOiJudW1iZXIiLCJuYW1lIjoiZHVyYXRpb24iLCJpZCI6ImE5N2UxMmY5LWVjNGQtNGIwMy1hN2JjLWVjODJmYjVjNDk5MSIsImRpc3BsYXktbmFtZSI6IkhvdXJzICh0cmFpbGluZykiLCJkZWZhdWx0IjoiMSIsInJlcXVpcmVkIjp0cnVlfX0sInF1ZXJ5IjoiV0lUSCBzdG9yeWJvYXJkX2V2ZW50IEFTIChcbiAgU0VMRUNUXG4gICAgaWQgQVMgc3Rvcnlib2FyZF9pZCxcbiAgICBkYXRhIC0-ICdjb3JlX3N0b3J5Ym9hcmRfZXhlY3V0aW9uJyAtPiAncGxhbicgLT4-ICdkaXNwbGF5TmFtZScgQVMgcGxhbixcbiAgICBqc29uYl9hcnJheV9lbGVtZW50cyhkYXRhIC0-ICdjb3JlX3N0b3J5Ym9hcmRfZXhlY3V0aW9uJyAtPiAnZXZlbnRzJykgQVMgZXZlbnRcbiAgRlJPTVxuICAgIHN0b3J5Ym9hcmRfZXhlY3V0aW9uX2luZm9cbiAgSk9JTiBlbnRpdHkgVVNJTkcgKGlkKVxuICBXSEVSRSBzdG9yeWJvYXJkX2V4ZWN1dGlvbl9pbmZvLmNyZWF0aW9uX2RhdGUgPiBub3coKSAtIGludGVydmFsICd7e2R1cmF0aW9ufX0gaG91cnMnIFxuKSwgZXZlbnRfbWV0cmljcyBBUyAoXG4gIFNFTEVDVFxuICAgIHN0b3J5Ym9hcmRfaWQsXG4gICAgcGxhbixcbiAgICBldmVudCAtPj4gJ2lkJyBBUyBldmVudF9pZCxcbiAgICBldmVudCAtPj4gJ2V2ZW50VHlwZScgQVMgZXZlbnRfdHlwZSxcbiAgICBldmVudCAtPiAnc291cmNlJyAtPj4gJ3N0b3J5SWQnIGFzIGV2ZW50X3N0b3J5X2lkLFxuICAgIGV2ZW50IC0-ICdzb3VyY2UnIC0-PiAnc2NlbmVJZCcgYXMgZXZlbnRfc2NlbmVfaWQsXG4gICAgZXZlbnQgLT4gJ3NvdXJjZScgLT4-ICd0YXNrSWQnIGFzIGV2ZW50X3Rhc2tfaWQsXG4gICAgZXZlbnQgLT4gJ2RldmljZUluZm8nIC0-PiAnbmFtZScgYXMgZXZlbnRfZGV2aWNlLFxuICAgIGV2ZW50IC0-ICdkZXZpY2VJbmZvJyAtPj4gJ2Nvbm5lY3Rpb25UeXBlJyBhcyBldmVudF9jb25uZWN0aW9uX3R5cGUsXG4gICAgZXZlbnQgLT4gJ2RldmljZUluZm8nIC0-PiAnYXBwVmVyc2lvbicgYXMgZXZlbnRfYXBwX3ZlcnNpb24sXG4gICAgKGV2ZW50IC0-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
    // See bucketed delays https://stats.withvector.com/question?duration=0.75#eyJkYXRhc2V0X3F1ZXJ5Ijp7Im5hdGl2ZSI6eyJ0ZW1wbGF0ZS10YWdzIjp7ImR1cmF0aW9uIjp7InR5cGUiOiJudW1iZXIiLCJuYW1lIjoiZHVyYXRpb24iLCJpZCI6IjE4NWNhZTQ2LTcwNDQtNDJhZi04NDVlLWJkYjA2YmEyNTRlNiIsImRpc3BsYXktbmFtZSI6IkR1cmF0aW9uIiwiZGVmYXVsdCI6IjQ4IiwicmVxdWlyZWQiOnRydWV9fSwicXVlcnkiOiJXSVRIIHN0b3J5Ym9hcmRfZXZlbnQgQVMgKFxuICBTRUxFQ1RcbiAgICBpZCBBUyBzdG9yeWJvYXJkX2lkLFxuICAgIGRhdGEgLT4gJ2NvcmVfc3Rvcnlib2FyZF9leGVjdXRpb24nIC0-ICdwbGFuJyAtPj4gJ2Rpc3BsYXlOYW1lJyBBUyBwbGFuLFxuICAgIGpzb25iX2FycmF5X2VsZW1lbnRzKGRhdGEgLT4gJ2NvcmVfc3Rvcnlib2FyZF9leGVjdXRpb24nIC0-ICdldmVudHMnKSBBUyBldmVudFxuICBGUk9NXG4gICAgc3Rvcnlib2FyZF9leGVjdXRpb25faW5mb1xuICBKT0lOIGVudGl0eSBVU0lORyAoaWQpXG4gIFdIRVJFIHN0b3J5Ym9hcmRfZXhlY3V0aW9uX2luZm8uY3JlYXRpb25fZGF0ZSA-IG5vdygpIC0gaW50ZXJ2YWwgJ3t7ZHVyYXRpb259fSBob3VycycgXG4pLCBldmVudF9tZXRyaWNzIEFTIChcbiAgU0VMRUNUXG4gICAgc3Rvcnlib2FyZF9pZCxcbiAgICBwbGFuLFxuICAgIGV2ZW50IC0-PiAnaWQnIEFTIGV2ZW50X2lkLFxuICAgIGV2ZW50IC0-PiAnZXZlbnRUeXBlJyBBUyBldmVudF90eXBlLFxuICAgIGV2ZW50IC0-ICdzb3VyY2UnIC0-PiAnc3RvcnlJZCcgYXMgZXZlbnRfc3RvcnlfaWQsXG4gICAgZXZlbnQgLT4gJ3NvdXJjZScgLT4-ICdzY2VuZUlkJyBhcyBldmVudF9zY2VuZV9pZCxcbiAgICBldmVudCAtPiAnc291cmNlJyAtPj4gJ3Rhc2tJZCcgYXMgZXZlbnRfdGFza19pZCxcbiAgICBldmVudCAtPiAnZGV2aWNlSW5mbycgLT4-ICduYW1lJyBhcyBldmVudF9kZXZpY2UsXG4gICAgZXZlbnQgLT4gJ2RldmljZUluZm8nIC0-PiAnY29ubmVjdGlvblR5cGUnIGFzIGV2ZW50X2Nvbm5lY3Rpb25fdHlwZSxcbiAgICBldmVudCAtPiAnZGV2aWNlSW5mbycgLT4-ICdhcHBWZXJzaW9uJyBhcyBldmVudF9hcHBfdmVyc2lvbixcbiAgICAoZXZlbnQgLT4-ICdwcm9jZXNzZWREYXRlJyk6OnRpbWVzdGFtcHR6IC0gKGV2ZW50IC0-PiAnY3JlYXRpb25EYXRlJyk6OnRpbWVzdGFtcHR6IEFTIHdhaXRfdGltZVxuICBGUk9NXG4gICAgc3Rvcnlib2FyZF9ldmVudFxuICBXSEVSRVxuICAgIGV2ZW50IC0-PiAnY3JlYXRpb25EYXRlJyBJUyBOT1QgTlVMTFxuICAgIEFORCBldmVudCAtPiAncHJvY2Vzc2VkRGF0ZScgSVMgTk9UIE5VTExcbiksXG5idWNrZXRlZCBBUyAoXG4gIFNFTEVDVFxuICAgICosXG4gICAgQUJTKEVYVFJBQ1QoRVBPQ0ggRlJPTSB3YWl0X3RpbWUpKSBBUyB3YWl0X3NlY29uZHNcbiAgRlJPTSBldmVudF9tZXRyaWNzXG4gIFdIRVJFIGV2ZW50X2FwcF92ZXJzaW9uID0gJzEwLjkuNjAzJ1xuKSxcbmJvdW5kcyBBUyAoXG4gIFNFTEVDVFxuICAgIE1JTih3YWl0X3NlY29uZHMpIEFTIG1pbl93YWl0LFxuICAgIE1BWCh3YWl0X3NlY29uZHMpIEFTIG1heF93YWl0XG4gIEZST00gYnVja2V0ZWRcbiksXG5idWNrZXRlZF9jb3VudHMgQVMgKFxuICBTRUxFQ1RcbiAgICB3aWR0aF9idWNrZXQoYi53YWl0X3NlY29uZHMsIGJvdW5kcy5taW5fd2FpdCwgYm91bmRzLm1heF93YWl0LCAxMCkgQVMgYnVja2V0LFxuICAgIENPVU5UKCopIEFTIGNvdW50LFxuICAgIE1JTihiLndhaXRfc2Vjb25kcykgQVMgYnVja2V0X21pbixcbiAgICBNQVgoYi53YWl0X3NlY29uZHMpIEFTIGJ1Y2tldF9tYXhcbiAgRlJPTSBidWNrZXRlZCBiLCBib3VuZHNcbiAgR1JPVVAgQlkgYnVja2V0XG4gIE9SREVSIEJZIGJ1Y2tldFxuKVxuU0VMRUNUXG4gIGJ1Y2tldCxcbiAgY291bnQsXG4gIFRPX0NIQVIoYnVja2V0X21pbiwgJ0ZNOTk5OTk5LjAwJykgfHwgJ3MgLSAnIHx8IFRPX0NIQVIoYnVja2V0X21heCwgJ0ZNOTk5OTk5LjAwJykgfHwgJ3MnIEFTIHJhbmdlXG5GUk9NIGJ1Y2tldGVkX2NvdW50czsifSwiZGF0YWJhc2UiOjE5LCJ0eXBlIjoibmF0aXZlIn0sImRpc3BsYXkiOiJyb3ciLCJkaXNwbGF5SXNMb2NrZWQiOnRydWUsInBhcmFtZXRlcnMiOlt7ImlkIjoiMTg1Y2FlNDYtNzA0NC00MmFmLTg0NWUtYmRiMDZiYTI1NGU2IiwidHlwZSI6Im51bWJlci89IiwidGFyZ2V0IjpbInZhcmlhYmxlIixbInRlbXBsYXRlLXRhZyIsImR1cmF0aW9uIl1dLCJuYW1lIjoiRHVyYXRpb24iLCJzbHVnIjoiZHVyYXRpb24iLCJkZWZhdWx0IjoiNDgiLCJyZXF1aXJlZCI6dHJ1ZX1dLCJ2aXN1YWxpemF0aW9uX3NldHRpbmdzIjp7ImdyYXBoLmRpbWVuc2lvbnMiOlsicmFuZ2UiXSwiZ3JhcGgubWV0cmljcyI6WyJjb3VudCJdfSwib3JpZ2luYWxfY2FyZF9pZCI6MzEzOSwidHlwZSI6InF1ZXN0aW9uIn0=
    // There, you can adjust the duration/window, and also the specific app version.
    //
    // NOTE: if creation vs. enqueue time is an important distinction, we'd need to add another timestamp field to events,
    // but sidestepping that here.
    for (const event of this.currEvents) {
      const trueTimeOffset = FormulaRegistry.getApplicationContext()?.getTruetimeOffset?.() ?? 0
      event.creationDate = moment().add(trueTimeOffset).toISOString()
    }

    // update the events with current events
    this.executionEvents = this.currEvents

    return Promise.resolve()
  }

  /**
   * Detect any state changes that are not tracked with any events yet and inject an synthetic event
   * so all the changes are tracked through events log
   *
   * Only run this callback for PUT request.  In theory, it should be run for PATCH as well, but let be conservative for
   * now since PUT request is outside the workflow request
   *
   * TODO: able to detect the state change automatically on the server side as well to inject synthetic event.  In
   * some cases, there are triggers that update the execution state and has to manually track it.  It would be good to
   * have a trigger to detect the changes and auto inject synthetic event.  The trigger should be run at the end of the pipeline
   * and do the following
   * 1. Somehow compare the execution state at very beginning of the pipeline and the state at the end, and the changes
   *    between the start and end are the ones from pipeline on the server side
   * 2. Dry-run and rebuild the execution state from the events
   * 3. Compare the rebuilt execution state and the actual one, the differences are the ones we should generate synthetic events for
   * 4. Generate synthetic events to capture the changes and inject back to the events log
   *
   * The basic idea is to self-heal/repair the events log so it could rebuild the execution state correctly
   *
   * or we could simplify the whole thing and just create a trigger on the server side to ensure the events log
   * is always in sync with the execution state.  If not, then it will inject the synthetic event to make it in sync
   * again
   *
   * Sample injected events
   *
   *        {
   *            "id": "cd4eeacb-0478-434e-9efc-a90850a2012d",
   *            "createdBy": { *                "entityId": "af0a7754-e64e-4eb5-b795-6d43da165523",
   *                "displayName": "Vector Support"
   *            },
   *            "eventType": "core_storyboard_event_synthetic",
   *            "creationDate": "2023-01-12T18:56:27.854Z",
   *            "outputMappings": [
   *                {
   *                    "value": {
   *                        "fieldA": "12"
   *                    },
   *                    "destination": "custom_tests_execution_kitchenSink.detailsEdit"
   *                }
   *            ]
   *        },
   *        {
   *            "id": "e66889eb-0c7a-4a3e-b36f-503878804676",
   *            "createdBy": {
   *                "entityId": "af0a7754-e64e-4eb5-b795-6d43da165523",
   *                "displayName": "Vector Support"
   *            },
   *            "eventType": "core_storyboard_event_synthetic",
   *            "creationDate": "2023-01-12T19:03:47.501Z",
   *            "outputMappings": [
   *                {
   *                    "value": "123",
   *                    "destination": "custom_tests_execution_kitchenSink.detailsEdit.fieldA"
   *                }
   *            ]
   *        }
   *
   * @param entity
   * @param props
   */
  private injectSyntheticEvents = async (entity: Entity, props: ISaveActionProps = {}) => {
    // TODO: narrow this to fix one particular use case, edit the execution outside the workflow in the details tab  as described in VD-8784
    // Need to run more tests supporting other use cases
    const isUpdateUsingPUT = RequestMethod.PUT && !entity.isNew
    if (!isUpdateUsingPUT) {
      return {}
    }

    // find the paths of the revelant changes only
    const paths = _.filter(
      entity.namespaces,
      (namespace) => namespace !== 'core_storyboard_execution' && namespace !== 'entity'
    )

    // detect the changes
    // most of the properties under core_storyboard_execution are managed by the server except for status which could
    // be overwrite by admin
    const statePatches = entity.jsonPatch(entity.prevContent, entity.content, {
      includedPaths: [...paths, 'core_storyboard_execution.status'],
    })

    if (_.isEmpty(statePatches)) {
      return {}
    }

    // convert execution state changes into synthetic events
    const syntheticEvents = [this.patchesToEvent(statePatches)]

    // convert synthetic events into json patches
    const syntheticPatch = _.map(syntheticEvents, (event) => this.eventToEventsLogPatch(event))

    // also modify the request method to PATCH to avoid any potential overwrite issue caused by PUT
    const { saveProps = {} } = props

    // for the synthetic patch generated from synthetic events, keep in a separate space so it won't tamper with the
    // the organic patch from the actual events
    return {
      ...props,
      method: RequestMethod.PATCH,
      saveProps: {
        ...saveProps,
        syntheticPatch: syntheticPatch,
      },
    }
  }

  private patchesToEvent(patches: any[] = []): Event {
    const event = this.createEventWithSource(null, 'core_storyboard_event_synthetic')
    const outputMappings = patches.map((patch) => this.patchToOutputMapping(patch))

    event.processedDate = event.creationDate
    event.outputMappings = outputMappings

    return event
  }

  private patchToOutputMapping(patch: any): Mapping {
    const { path, value } = patch
    const objectPath = path.replace(/\//g, '.').slice(1)

    return {
      value,
      destination: objectPath,
    }
  }

  private eventToEventsLogPatch(event: Event): any {
    return {
      op: 'add',
      path: '/core_storyboard_execution/events/-',
      value: event,
    }
  }
}
